import React from 'react';
import moment from 'moment';
import { IntlShape, useIntl, FormattedMessage } from 'react-intl'

import { useTrackingData } from '../dataProviders';
import { makeBtStyles } from '../Theme';



const deliveryDateOrToday = ({ estimated_delivery_date }: { estimated_delivery_date: string }, intl: IntlShape) => {
  let isToday = moment(estimated_delivery_date).isSame(moment(), 'day');

  if( isToday ) {
    const text = intl.formatMessage({
      description: 'estimatedDateOrStatus.deliveryEstimatedToday',
      defaultMessage: 'Today'
    });
    return { text }
  }
  else {
    return { date: estimated_delivery_date }
  }
};


function useMessageAndStatus(source: string | null, status_code: string, estimated_delivery_date: string | undefined | null, hasAnyEvents: boolean) {
  const intl = useIntl();

  const defaultStatusText = intl.formatMessage({
    description: 'estimatedDateOrStatus.defaultStatus',
    defaultMessage: 'Ready to Go'
  });
  const defaultStatus = { text: defaultStatusText, status: 'isReadyToGo' };

  if( !source || source.toLowerCase() !== 'shipengine') {
    return defaultStatus;
  }

  status_code = status_code.toUpperCase();

  switch( status_code ) {
    case 'AC': {
      if( estimated_delivery_date ) {
        return { ...deliveryDateOrToday({ estimated_delivery_date }, intl), status: 'isAccepted' };
      }
      return { text: defaultStatusText, status: 'isAccepted' };
    }
    case 'IT': {
      if( estimated_delivery_date ) {
        return { ...deliveryDateOrToday({ estimated_delivery_date }, intl), status: 'isInTransit' };
      }
      const text = intl.formatMessage({
        description: 'estimatedDateOrStatus.inTransitButNoDate',
        defaultMessage: 'In Transit'
      });
      return { text, status: 'isInTransit' };
    }
    case 'DE':
      const text = intl.formatMessage({
        description: 'estimatedDateOrStatus.delivered',
        defaultMessage: 'Delivered'
      });
      return { text, status: 'isDelivered' };
    case 'EX':
    case 'UN':
      if( estimated_delivery_date ) {
        return { ...deliveryDateOrToday({ estimated_delivery_date }, intl), status: 'isInTransit' };
      }
      else if( hasAnyEvents ) {
        const text = intl.formatMessage({
          description: 'estimatedDateOrStatus.exceptionOrUnknownNoDateHasEvents',
          defaultMessage: 'In Transit'
        });
        return { text, status: 'isInTransit' };
      }
      else {
        const text = intl.formatMessage({
          description: 'estimatedDateOrStatus.exceptionOrUnknownNoDateNoEvents',
          defaultMessage: 'Status Unavailable'
        });
        return { text, status: 'isUnknown' };
      }
    case 'AT': {
      const text = intl.formatMessage({
        description: 'estimatedDateOrStatus.attemptedDelivery',
        defaultMessage: 'We Missed You'
      });
      return { text, status: 'isDeliveryAttempted' };
    }
    default:
      return defaultStatus;
  }
}

const useStyles = makeBtStyles( theme => ({
  root: {
    ...theme.characteristicFont,
    ...theme.characteristicColor,
    '&:not(.isDelivered):not(.isInTransit):not(.estimatedDeliveryDate)': {
      color: '#929292',
      fontWeight: 'normal'
    }
  }
}));

export default function EstimatedDeliveryDateOrStatus({ className='', children }:{
  className?: string,
  children?: ({ date, text, status, formattedStatusOrDate }:{
    date?: string,
    text?: string,
    status: string,
    formattedStatusOrDate?: React.ReactNode
  }) => React.ReactNode
}) {
  const intl = useIntl();
  const classes = useStyles();
  const { source=null, status_code='', estimated_delivery_date=null, events=[] } = useTrackingData();

  const { date, text, status }: { date?: string, text?: string, status: string } = useMessageAndStatus( source, status_code, estimated_delivery_date, !!events.length )

  const dayOfWeek = date && intl.formatDate(date, { weekday: 'long' });
  const monthName = date && intl.formatDate(date, { month: 'long' });
  const dayOfMonth = date && intl.formatDate(date, { day: 'numeric' });

  const formattedStatusOrDate = ( date ? <FormattedMessage
    description="formattedEstimatedDeliveryDate"
    defaultMessage="<row>{dayOfWeek}, </row><row>{monthName} {dayOfMonth}</row>"
    values={{ dayOfWeek, monthName, dayOfMonth, row: (chunks: any) => <span>{chunks}</span> }}
  /> : (
    text
  ));

  const title = date && intl.formatMessage({
    description: "estimatedDeliveryDateHoverTitle",
    defaultMessage: "Estimated delivery: {date}"
  }, { date: intl.formatDate(date, { weekday: 'long', month: 'long', year: 'numeric', day: 'numeric' }) });

  return (
    <div
      className={`${classes.root} ${className} ${status}`}
      data-testid="estimated-delivery-date-or-status"
      title={title}
    >
      { children ? (
        children({ date, text, status, formattedStatusOrDate })
      ):(
        formattedStatusOrDate
      )}
    </div>
  );
}